import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LogIn, FileText, Phone, Mail, ChevronRight, MapPin, ShieldCheck } from "lucide-react";
import { useRFQ } from "../components/RFQProvider";
import { COMPANY } from "../lib/brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

// External link — opens in a new tab, so it's rendered as a plain <a>
// instead of an internal <NavLink>.
const EXTERNAL_NAV = {
  label: "AFDSS Portal",
  href: "https://afdss.ueeindia.com/",
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { openRFQ } = useRFQ();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl shadow-[0_8px_28px_-12px_rgba(10,19,34,0.18)]"
          : "bg-white"
      }`}
    >
      {/* PREMIUM TOP CONTACT STRIP */}
      <div className="contact-strip bg-gradient-to-r from-carbon-950 via-steel-900 to-carbon-950 text-slate-200 text-[11px] hidden md:block border-b border-amber-500/20">
        <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-6 2xl:px-10 py-2.5 flex justify-between items-center gap-4 font-mono">
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-2 h-2 bg-emerald-400 rounded-full pulse-dot shrink-0" />
            <ShieldCheck className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span className="text-amber-400 tracking-[0.22em] font-bold whitespace-nowrap">
              MINING SAFETY SPECIALIST
            </span>
            <span className="text-slate-600">·</span>
            <span className="hidden lg:inline-flex items-center gap-1.5 text-slate-300 truncate">
              <MapPin className="w-3 h-3 text-amber-500 shrink-0" />
              <span className="truncate">Bengaluru, Karnataka</span>
            </span>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="topbar-phone"
              className="inline-flex items-center gap-1.5 hover:text-amber-400 transition-colors"
            >
              <Phone className="w-3 h-3" />
              <span className="font-semibold tracking-wide">{COMPANY.phone}</span>
            </a>
            <span className="text-slate-700 hidden md:inline">·</span>
            <a
              href={`tel:${COMPANY.phone2Raw}`}
              data-testid="topbar-phone-2"
              className="hidden md:inline-flex items-center gap-1.5 hover:text-amber-400 transition-colors"
            >
              <Phone className="w-3 h-3" />
              <span className="font-semibold tracking-wide">{COMPANY.phone2}</span>
            </a>
            <span className="text-slate-700">|</span>
            <a
              href={`mailto:${COMPANY.email1}`}
              data-testid="topbar-email-1"
              className="inline-flex items-center gap-1.5 hover:text-amber-400 transition-colors"
            >
              <Mail className="w-3 h-3" />
              {COMPANY.email1}
            </a>
            <span className="hidden lg:inline text-slate-700">|</span>
            <a
              href={`mailto:${COMPANY.email2}`}
              data-testid="topbar-email-2"
              className="hidden lg:inline-flex items-center gap-1.5 hover:text-amber-400 transition-colors"
            >
              <Mail className="w-3 h-3" />
              {COMPANY.email2}
            </a>
          </div>
        </div>
      </div>

      {/* MAIN NAVBAR */}
      <div className="max-w-[1600px] 2xl:max-w-[1760px] mx-auto px-4 md:px-6 2xl:px-10 h-[64px] md:h-[78px] flex items-center justify-between gap-3">
        {/* Wordmark logo */}
        <Link to="/" className="flex items-center shrink-0 group min-w-0 max-w-[60%] md:max-w-none" data-testid="navbar-logo">
          <div className="wordmark">
            <span className="wm-uni">UNIVERSAL</span>
            <span className="wm-elec">ELECTRICALS &amp; ELECTRONICS</span>
          </div>
        </Link>

        {/* Center nav (desktop only) */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `nav-link text-[11px] font-bold uppercase tracking-[0.12em] ${isActive ? "is-active" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <a
            href={EXTERNAL_NAV.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-afdss-portal"
            className="nav-link text-[11px] font-bold uppercase tracking-[0.12em]"
          >
            {EXTERNAL_NAV.label}
          </a>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 md:gap-2.5 shrink-0">
          {/* Get Quote — only on xl screens (≥1280) */}
          <button
            data-testid="navbar-rfq-btn"
            onClick={() => openRFQ()}
            className="hidden xl:inline-flex btn-amber-pill"
          >
            <FileText className="w-4 h-4" /> Get Quote
          </button>
          {/* Login — only on lg screens (≥1024) so mobile/tablet only show hamburger */}
          <Link
            to="/login"
            data-testid="navbar-login-btn"
            className="hidden lg:inline-flex btn-login-pill"
          >
            <LogIn className="w-4 h-4" /> Login
          </Link>
          {/* Hamburger — visible on every screen below lg */}
          <button
            data-testid="navbar-menu-toggle"
            className="lg:hidden inline-flex w-11 h-11 items-center justify-center text-carbon hover:bg-slate-100 active:bg-slate-200 transition-colors -mr-1"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Bottom amber accent line */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-amber-500 to-transparent" />

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `py-3 px-1 text-sm font-bold uppercase tracking-wider border-l-2 pl-3 flex items-center justify-between group ${
                    isActive ? "border-amber-500 text-amber-600" : "border-transparent text-carbon"
                  }`
                }
              >
                <span>{n.label}</span>
                <ChevronRight className="w-4 h-4 opacity-30 group-hover:opacity-100 group-hover:text-amber-500 transition-opacity" />
              </NavLink>
            ))}
            <a
              href={EXTERNAL_NAV.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-afdss-portal"
              className="py-3 px-1 text-sm font-bold uppercase tracking-wider border-l-2 border-transparent pl-3 flex items-center justify-between group text-carbon"
            >
              <span>{EXTERNAL_NAV.label}</span>
              <ChevronRight className="w-4 h-4 opacity-30 group-hover:opacity-100 group-hover:text-amber-500 transition-opacity" />
            </a>
            <Link
              to="/login"
              data-testid="mobile-login-btn"
              className="mt-3 w-full inline-flex btn-login-pill justify-center"
            >
              <LogIn className="w-4 h-4" /> Login
            </Link>
            <button
              data-testid="mobile-rfq-btn"
              onClick={() => openRFQ()}
              className="w-full inline-flex btn-amber-pill justify-center mt-2"
            >
              <FileText className="w-4 h-4" /> Get Quote
            </button>

            <div className="mt-5 pt-4 border-t border-slate-200 grid grid-cols-1 gap-2 text-xs text-slate-600">
              <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2"><Phone className="w-3 h-3 text-amber-500" /> {COMPANY.phone}</a>
              <a href={`tel:${COMPANY.phone2Raw}`} className="inline-flex items-center gap-2"><Phone className="w-3 h-3 text-amber-500" /> {COMPANY.phone2}</a>
              <a href={`mailto:${COMPANY.email1}`} className="inline-flex items-center gap-2"><Mail className="w-3 h-3 text-amber-500" /> {COMPANY.email1}</a>
              <a href={`mailto:${COMPANY.email2}`} className="inline-flex items-center gap-2"><Mail className="w-3 h-3 text-amber-500" /> {COMPANY.email2}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}